import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states';
import { BoardDateNav } from '@/components/dispatch/BoardDateNav';
import { DayBoard } from '@/components/dispatch/DayBoard';
import { WeekBoard } from '@/components/dispatch/WeekBoard';
import { UnassignedRail } from '@/components/dispatch/UnassignedRail';
import { ViewToggle, type BoardView } from '@/components/dispatch/ViewToggle';
import { MoveJobDialog, type MoveRequest } from '@/components/dispatch/MoveJobDialog';
import { useJobDrag, type DropTarget } from '@/components/dispatch/useJobDrag';
import { JobCard } from '@/components/dispatch/JobCard';
import { SLOT_MINUTES, hoursForDate, occursOnDay, visibleRange } from '@/lib/dispatchBoard';
import { boardItems, jobCount, type BoardItem } from '@/lib/boardItems';
import {
  detectConflicts,
  type ConflictMap,
  type JobConflict,
} from '@/lib/conflicts';
import {
  parseIsoDate,
  toIsoDate,
  todayInZone,
  zonedTimeToInstant,
  zonedMinuteOfDay,
  type CivilDate,
} from '@/lib/timezone';
import { usePlaceJob, type Placement } from './dispatch/usePlaceJob';
import { useHasPermission } from '@/auth/RequirePermission';
import { Permissions } from '@/auth/permissions';
import {
  useAccount,
  useBoardDay,
  useBoardJobs,
  useBoardWeek,
  useTechnicianDays,
  useTechnicians,
} from './dispatch/useDispatchBoard';

/**
 * The manual dispatch board, day view (M1-09).
 *
 * Manual at launch — auto-routing is M5, per the Timeline. This screen shows
 * the day and nothing more: assigning and rescheduling by dragging is M1-11,
 * and conflict flagging is M1-12.
 *
 * The date, the technician filter and the chosen view all live in the URL
 * rather than in component state. That is what makes M1-10's "the toggle
 * preserves the selected technician filter and date" true by construction --
 * switching view rewrites one query parameter and leaves the other two alone,
 * so there is no state anybody has to remember to carry across. It also means
 * a dispatcher pasting "the board for Thursday" into a chat message sends the
 * board for Thursday, and the browser's Back button walks back through the
 * days someone paged through rather than leaving the screen.
 */
export function Dispatch() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const account = useAccount();
  const technicians = useTechnicians();
  // M2-21 rule 5c, held here too: the bounced marker is the office's. The API
  // sends anyone else false; a server that leaked it still would not reach a
  // card (the job page's reader rule, on the board).
  const isOffice = useHasPermission()(Permissions.manageCustomers);

  const timeZone = account.data?.timezone;
  // The account's today, not the viewer's machine's: at 11pm Central a
  // dispatcher in Denver should still open on the business's current day.
  const today = timeZone ? todayInZone(timeZone) : null;

  const requestedDate = parseIsoDate(searchParams.get('date') ?? '');
  const date: CivilDate | null = requestedDate ?? today;
  // Anything that is not "week" is the day view, a hand-edited URL included.
  // The day is the safe default: it is the more detailed of the two, so
  // falling back to it can never hide work.
  const view: BoardView = searchParams.get('view') === 'week' ? 'week' : 'day';

  const day = useBoardDay(date, timeZone);
  const week = useBoardWeek(date, timeZone);

  // ONE request for availability, licences and skills across whichever days
  // are on screen (M1-39) — not one per technician (M1-37's rule) and not one
  // per day, which is why the API takes a window. The week view needs a state
  // per technician per day, because that is the granularity it greys at.
  const technicianDays = useTechnicianDays(
    view === 'week'
      ? week && { from: week.days[0]!.date, to: week.days[6]!.date }
      : date && { from: date, to: date },
  );
  const board = technicianDays.data;
  // Told apart so the tooltip can say "still loading" or "couldn't be loaded"
  // instead of "none recorded" — the board itself degrades identically either
  // way (nothing greyed, no warnings), which is the point, but a dispatcher
  // reading a licence list needs to know which of the three they are seeing.
  const detailStatus = technicianDays.isPending
    ? ('pending' as const)
    : technicianDays.isError
      ? ('unavailable' as const)
      : ('ready' as const);

  // One request per view, not seven for the week. The week asks the same
  // windowed List endpoint with a wider window, and WeekBoard splits the
  // result back into days using the same predicate the API filtered on.
  const jobs = useBoardJobs(
    view === 'week'
      ? week && { from: week.weekStart, to: week.weekEnd, key: `week:${toIsoDate(week.startDate)}` }
      : day && { from: day.dayStart, to: day.dayEnd, key: `day:${toIsoDate(day.date)}` },
  );

  const technicianFilter = searchParams.get('tech');

  function setParam(key: string, value: string | null) {
    const next = new URLSearchParams(searchParams);
    if (value === null) {
      next.delete(key);
    } else {
      next.set(key, value);
    }
    // `replace: false` — paging through days should be walkable with Back.
    setSearchParams(next);
  }

  // The board's unit is a VISIT since M2-52 (Kyle, 2026-09-25): a three-day
  // repipe is three cards, each under that day's technician, and a job with
  // no visits yet is one card in the rail. Everything below — the rail, the
  // grid's hours, the conflict rules, the drag — works on these cards.
  //
  // ⚠️ Filtered to the days ON SCREEN, once, here. The API returns a job when
  // ANY of its visits is in the window, and it returns ALL of that job's
  // visits — so Tuesday's request brings the repipe's Wednesday too. Before
  // visits the board could trust that everything it was handed belonged to
  // the window; a card per visit cannot. Unfiltered, Wednesday's card was
  // drawn on Tuesday (clamped against the bottom of the grid), stretched the
  // day's hours to midnight, and sat in Tuesday's rail if nobody was on it.
  // A job with no visits belongs to no day and always comes along for the rail.
  const allItems = useMemo(() => {
    const onScreen = view === 'week' ? (week?.days ?? []) : day ? [day] : [];
    return boardItems(jobs.data ?? [])
      .filter((item) => item.scheduledStart === null || onScreen.some((shown) => occursOnDay(item, shown)))
      .map((item) => (isOffice || !item.emailBounced ? item : { ...item, emailBounced: false }));
  }, [jobs.data, view, week, day, isOffice]);

  const visibleTechnicians = useMemo(() => {
    const all = technicians.data ?? [];
    return technicianFilter ? all.filter((user) => user.id === technicianFilter) : all;
  }, [technicians.data, technicianFilter]);

  // The rail is assignment, not scheduling: a visit with a time but nobody on
  // it belongs here just as much as a job with neither, because in both cases
  // nobody is going to turn up. Per VISIT (Kyle, 2026-09-25): a repipe whose
  // Tuesday has nobody booked shows that Tuesday here, even with Monday's
  // technician in place.
  const unassigned = useMemo(
    () => allItems.filter((item) => item.assignedUserId === null),
    [allItems],
  );

  // Only the day view draws a time axis; the week's cells are lists.
  const range = useMemo(() => {
    if (!day || !account.data || !date || view === 'week') {
      return null;
    }
    // Sized against ALL of the day's jobs, not just the filtered technicians':
    // narrowing to one technician should not silently change what hours the
    // grid covers under a dispatcher who is comparing them.
    const scheduled = allItems.filter((item) => item.scheduledStart !== null);
    return visibleRange(hoursForDate(account.data.businessHours.days, date), scheduled, day);
  }, [day, account.data, date, allItems, view]);

  /**
   * What clashes, across whichever days are on screen (M1-12).
   *
   * Computed over ALL the day's jobs rather than the filtered technician's:
   * narrowing to one person must not make their double-booking disappear,
   * because the other half of the clash is still on the same technician and
   * still real. It is also recomputed from `jobs.data`, which the optimistic
   * move already patched — so a drag that creates a conflict flags it as the
   * card lands, not a round trip later.
   */
  const conflicts: ConflictMap = useMemo(() => {
    if (!account.data) {
      return new Map<string, JobConflict[]>();
    }
    const days = view === 'week' ? (week?.days ?? []) : day ? [day] : [];
    // Per card: two visits on one technician at one moment clash whether they
    // belong to one job or two.
    return detectConflicts(
      allItems,
      days,
      account.data.businessHours.days,
      // M1-39's "scheduled onto somebody who is not working" warning rides the
      // SAME mechanism, as the AC asks — so it lands in the existing badge,
      // the existing card label and the existing header count with no new
      // plumbing. Passing the map in rather than a second detector is what
      // keeps that true.
      board?.availability,
    );
  }, [allItems, view, week, day, account.data, board]);

  // "N jobs need a look" counts JOBS: the map is per card, so a repipe out of
  // hours on all three of its days is three entries but one job (M2-52).
  const conflictedJobs = useMemo(
    () => jobCount(allItems.filter((item) => conflicts.has(item.id))),
    [allItems, conflicts],
  );

  const isError = account.isError || technicians.isError || jobs.isError;
  // `jobs` only counts as pending once it is actually enabled. A disabled
  // react-query query sits at `pending` forever, so counting it unconditionally
  // left the board spinning for good when the account request failed — the one
  // failure that also stops the jobs query from ever starting. Errors are
  // checked first for the same reason: whichever query failed, the answer is
  // the error state, not a spinner that will never resolve.
  const isPending =
    !isError && (account.isPending || technicians.isPending || (day !== null && jobs.isPending));

  // The owner dashboard's "N jobs waiting to be scheduled" links here as
  // `#unassigned-rail` (M2-17). The browser's own jump to a fragment happens
  // at navigation, before the board has loaded and drawn the rail, so it
  // finds nothing. This waits for the board, then brings the rail into view
  // and moves focus there so a keyboard or screen-reader user lands on it too.
  const { hash } = useLocation();
  const railRequested = hash === '#unassigned-rail';
  const boardReady = !isPending && !isError;
  useEffect(() => {
    if (!railRequested || !boardReady) {
      return;
    }
    const rail = document.getElementById('unassigned-rail');
    rail?.scrollIntoView?.({ block: 'start' });
    rail?.focus({ preventScroll: true });
  }, [railRequested, boardReady]);

  // ---- Moving a job (M1-11) --------------------------------------------

  const placementQueryKey = useMemo(
    () => [
      'jobs',
      'board',
      view === 'week'
        ? week && `week:${toIsoDate(week.startDate)}`
        : day && `day:${toIsoDate(day.date)}`,
    ],
    [view, week, day],
  );
  const placeJob = usePlaceJob(placementQueryKey);

  const [movingJobId, setMovingJobId] = useState<string | null>(null);
  // A polite live region rather than a toast: a move is a small, frequent act,
  // and a dispatcher doing twenty of them does not want twenty things to
  // dismiss. It has to be ANNOUNCED though — an optimistic card that slid to a
  // new column says nothing to someone who cannot see it move.
  const [announcement, setAnnouncement] = useState('');

  const movingJob = movingJobId === null ? null : (findCard(allItems, movingJobId) ?? null);

  const place = useCallback(
    (placement: Placement, describeDestination: string) => {
      const customer = placement.item.customerName;
      // Taking one day off a multi-day job leaves the job on the calendar, so
      // it says so rather than "took the job off the schedule" — and moving
      // one day says WHICH day moved, not that the job did: the card's own
      // "Visit 2 of 3", for someone who cannot see which card slid.
      const oneOfSeveral =
        placement.item.visitCount > 1
          ? { number: placement.item.visitNumber, total: placement.item.visitCount }
          : null;
      placeJob.mutate(placement, {
        onSuccess: () =>
          setAnnouncement(
            placement.scheduledStart === null
              ? oneOfSeveral
                ? t('dispatch.move.visitRemoved', { customer })
                : t('dispatch.move.unscheduled', { customer })
              : placement.assignedUserId === null
                ? oneOfSeveral
                  ? t('dispatch.move.movedVisitUnassigned', { customer, ...oneOfSeveral })
                  : t('dispatch.move.movedUnassigned', { customer })
                : oneOfSeveral
                  ? t('dispatch.move.movedVisit', { customer, destination: describeDestination, ...oneOfSeveral })
                  : t('dispatch.move.moved', { customer, destination: describeDestination }),
          ),
        onError: () => setAnnouncement(t('dispatch.move.failed', { customer })),
      });
    },
    [placeJob, t],
  );

  /**
   * Turns a drop onto a cell into a placement.
   *
   * The job's DURATION is preserved rather than its end time: a two-hour call
   * dropped at ten runs to twelve. Carrying the old end across would produce a
   * job that ends before it starts the moment anything moved later in the day.
   */
  const placeAtTarget = useCallback(
    (cardId: string, target: DropTarget) => {
      const item = findCard(allItems, cardId);
      if (!item || !timeZone) {
        return;
      }

      // The rail: no technician, no date — keep the schedule, drop the person.
      if (target.technicianId === null && target.isoDate === undefined) {
        place(
          {
            item,
            scheduledStart: item.scheduledStart,
            scheduledEnd: item.scheduledEnd,
            assignedUserId: null,
            assignedUserName: null,
          },
          '',
        );
        return;
      }

      const targetDate = target.isoDate ? parseIsoDate(target.isoDate) : date;
      if (!targetDate) {
        return;
      }

      // The week view has no slots, so a job keeps the time of day it already
      // had; the day view supplies the slot the pointer landed in.
      const minute =
        target.slotMinute ??
        (item.scheduledStart ? zonedMinuteOfDay(new Date(item.scheduledStart), timeZone) : SLOT_MINUTES * 16);

      const start = zonedTimeToInstant(targetDate, Math.floor(minute / 60), minute % 60, timeZone);
      const durationMs = durationOf(item);

      const technicianName =
        (technicians.data ?? []).find((user) => user.id === target.technicianId)?.name ?? '';

      place(
        {
          item,
          scheduledStart: start.toISOString(),
          scheduledEnd: durationMs === null ? null : new Date(start.getTime() + durationMs).toISOString(),
          assignedUserId: target.technicianId,
          assignedUserName: technicianName || null,
        },
        technicianName,
      );
    },
    [allItems, date, place, technicians.data, timeZone],
  );

  const { drag, handlersFor } = useJobDrag({
    onDrop: placeAtTarget,
    onActivate: setMovingJobId,
  });

  const dragBinding = useMemo(
    () => ({ draggingCardId: drag?.cardId ?? null, handlersFor }),
    [drag, handlersFor],
  );

  const draggingJob = drag ? findCard(allItems, drag.cardId) : undefined;

  const onMoveFromDialog = useCallback(
    (request: MoveRequest) => {
      const item = movingJob;
      if (!item || !timeZone) {
        return;
      }
      setMovingJobId(null);

      const technicianName =
        (technicians.data ?? []).find((user) => user.id === request.technicianId)?.name ?? '';

      // "Take it off the schedule": a visit card REMOVES that visit (Kyle,
      // 2026-09-25); an unscheduled job's card just takes the technician.
      if (request.isoDate === null) {
        place(
          {
            item,
            scheduledStart: null,
            scheduledEnd: null,
            assignedUserId: request.technicianId,
            assignedUserName: technicianName || null,
          },
          technicianName,
        );
        return;
      }

      const targetDate = parseIsoDate(request.isoDate);
      if (!targetDate) {
        return;
      }
      const start = zonedTimeToInstant(
        targetDate,
        Math.floor(request.minute / 60),
        request.minute % 60,
        timeZone,
      );
      const durationMs = durationOf(item);

      place(
        {
          item,
          scheduledStart: start.toISOString(),
          scheduledEnd:
            durationMs === null ? null : new Date(start.getTime() + durationMs).toISOString(),
          assignedUserId: request.technicianId,
          assignedUserName: technicianName || null,
        },
        technicianName,
      );
    },
    [movingJob, place, technicians.data, timeZone],
  );


  return (
    <section className="space-y-4">
      <div className="space-y-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{t('nav.dispatch')}</h1>
          <p className="text-sm text-[var(--color-muted-foreground)]">{t('dispatch.subtitle')}</p>
        </div>

        {date && day && week && timeZone && today ? (
          <div className="flex flex-wrap items-center gap-3">
            <ViewToggle
              view={view}
              // The day view is the default, so it is the ABSENCE of the
              // parameter rather than `view=day`. One canonical URL per board.
              onChange={(next) => setParam('view', next === 'day' ? null : next)}
            />

            <BoardDateNav
              date={date}
              dayStart={day.dayStart}
              weekFirstDayStart={week.weekStart}
              weekLastDayStart={week.days[6]!.dayStart}
              view={view}
              timeZone={timeZone}
              today={today}
              onChange={(next) => setParam('date', toIsoDate(next))}
            />

            <label className="flex items-center gap-2 text-sm">
              <span className="text-[var(--color-muted-foreground)]">
                {t('dispatch.technicianFilter')}
              </span>
              <select
                className="h-8 rounded-md border border-[var(--color-input)] bg-transparent px-2 text-sm focus-visible:ring-2 focus-visible:ring-[var(--color-ring)] focus-visible:outline-none"
                value={technicianFilter ?? ''}
                onChange={(event) => setParam('tech', event.target.value || null)}
              >
                <option value="">{t('dispatch.allTechnicians')}</option>
                {(technicians.data ?? []).map((technician) => (
                  <option key={technician.id} value={technician.id}>
                    {technician.name}
                  </option>
                ))}
              </select>
            </label>
          </div>
        ) : null}

        {conflictedJobs > 0 ? (
          // Announced as well as shown: a dispatcher who cannot see the badges
          // still needs to know something on this board wants a second look.
          // `role="status"`, not `alert` — nothing here is urgent, and nothing
          // is blocked.
          <p
            role="status"
            className="rounded-md border border-[var(--color-destructive)] px-3 py-2 text-sm text-[var(--color-destructive)]"
            data-testid="conflict-summary"
          >
            <span aria-hidden="true">⚠ </span>
            {t('dispatch.conflict.summary', { count: conflictedJobs })}{' '}
            <span className="text-[var(--color-muted-foreground)]">
              {t('dispatch.conflict.summaryDetail')}
            </span>
          </p>
        ) : null}

        {range?.isClosedDay ? (
          <p
            className="rounded-md bg-[var(--color-muted)] px-3 py-2 text-sm text-[var(--color-muted-foreground)]"
            data-testid="closed-day-notice"
          >
            {t('dispatch.closedDay')}
          </p>
        ) : null}
      </div>

      {isError ? (
        <ErrorState
          title={t('dispatch.loadErrorTitle')}
          description={t('dispatch.loadErrorDescription')}
          onRetry={() => {
            void account.refetch();
            void technicians.refetch();
            void jobs.refetch();
          }}
        />
      ) : isPending ? (
        <LoadingState label={t('dispatch.loading')} />
      ) : !day || !week || !timeZone || !account.data ? null : (
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
          <div className="min-w-0 flex-1">
            {visibleTechnicians.length === 0 ? (
              <EmptyState
                title={
                  technicianFilter
                    ? t('dispatch.noMatchingTechnicianTitle')
                    : t('dispatch.noTechniciansTitle')
                }
                description={
                  technicianFilter
                    ? t('dispatch.noMatchingTechnicianDescription')
                    : t('dispatch.noTechniciansDescription')
                }
              />
            ) : view === 'week' ? (
              <WeekBoard
                week={week}
                technicians={visibleTechnicians}
                jobs={allItems}
                businessHours={account.data.businessHours.days}
                timeZone={timeZone}
                dragBinding={dragBinding}
                conflicts={conflicts}
                board={board}
                detailStatus={detailStatus}
                // Following a day heading drills into that day and keeps the
                // technician filter -- the same "one parameter changes" rule
                // the toggle itself follows.
                onSelectDay={(isoDate) => {
                  const next = new URLSearchParams(searchParams);
                  next.set('date', isoDate);
                  next.delete('view');
                  setSearchParams(next);
                }}
              />
            ) : range ? (
              <DayBoard
                day={day}
                range={range}
                technicians={visibleTechnicians}
                jobs={allItems}
                timeZone={timeZone}
                dragBinding={dragBinding}
                conflicts={conflicts}
                board={board}
                detailStatus={detailStatus}
              />
            ) : null}
          </div>

          <UnassignedRail
            jobs={unassigned}
            timeZone={timeZone}
            dragBinding={dragBinding}
            conflicts={conflicts}
          />
        </div>
      )}

      {/* Announced, never shown: the visual feedback is the card arriving
          where it was dropped. */}
      <p role="status" aria-live="polite" className="sr-only" data-testid="dispatch-announcement">
        {announcement}
      </p>

      {/* The card that follows the pointer. `pointer-events: none` is load
          bearing — without it this is what sits under the cursor at drop time
          and the board never sees the cell underneath. */}
      {drag && draggingJob && timeZone ? (
        <div
          aria-hidden="true"
          className="pointer-events-none fixed z-50 w-40 opacity-90 shadow-lg"
          style={{ left: drag.x + 8, top: drag.y + 8 }}
        >
          <JobCard job={draggingJob} timeZone={timeZone} />
        </div>
      ) : null}

      {movingJob && timeZone && date ? (
        <MoveJobDialog
          job={movingJob}
          technicians={technicians.data ?? []}
          date={date}
          timeZone={timeZone}
          slotRange={range ?? { startMinute: 0, endMinute: 24 * 60 }}
          onMove={onMoveFromDialog}
          onClose={() => setMovingJobId(null)}
        />
      ) : null}
    </section>
  );
}

/**
 * A card by its id, surviving the one re-key a card goes through while
 * someone may be holding it. A job placed through `/schedule` is keyed by its
 * JOB id until the refetch brings its real visit (`boardItems`' pending
 * case), and by that visit's id after — so a dialog opened, or a drag started,
 * on the optimistic card looked for an id that no longer existed: the dialog
 * vanished mid-edit and the drop did nothing (found in review, M2-52).
 */
function findCard(items: readonly BoardItem[], cardId: string): BoardItem | undefined {
  return (
    items.find((item) => item.id === cardId) ??
    items.find((item) => item.jobId === cardId && item.visitCount === 1)
  );
}

/**
 * A card's length, kept across a move: a two-hour visit dropped at ten runs
 * to twelve. Null for a point in time, which stays one.
 */
function durationOf(item: { scheduledStart: string | null; scheduledEnd: string | null }): number | null {
  return item.scheduledStart && item.scheduledEnd
    ? new Date(item.scheduledEnd).getTime() - new Date(item.scheduledStart).getTime()
    : null;
}
